/**
 * RTWO Indexer Engine — Service Factory
 */

import { indexerInternalEventBus } from '../events/indexer-event-bus';
import {
  eventBusAdapterRegistry,
  ledgerAdapterRegistry,
  protocolAdapterRegistry,
  storageAdapterRegistry,
  type EventBusAdapterRegistry,
  type LedgerAdapterRegistry,
  type ProtocolAdapterRegistry,
  type StorageAdapterRegistry,
} from '../registry/adapter-registries';
import {
  createIndexerRepositories,
  type IndexerRepositories,
} from '../repositories/indexer-repositories';
import { IndexJobRegistryService } from './index-job-registry-service';
import { BlockIndexingService } from './block-indexing-service';
import { TransactionIndexingService } from './transaction-indexing-service';
import { AddressIndexingService } from './address-indexing-service';
import { AssetIndexingService } from './asset-indexing-service';
import { EventIndexingService } from './event-indexing-service';
import { IndexQueryService } from './index-query-service';
import { IndexSearchService } from './index-search-service';
import { IndexStorageBindingService } from './index-storage-binding-service';
import { IndexValidationService } from './index-validation-service';
import { IndexerEventPublisherService } from './indexer-event-publisher-service';
import { IndexerReadinessService } from './indexer-readiness-service';
import type { IndexerServiceContext } from './base-indexer-service';

export interface IndexerEngineServices {
  jobRegistry: IndexJobRegistryService;
  blockIndexing: BlockIndexingService;
  transactionIndexing: TransactionIndexingService;
  addressIndexing: AddressIndexingService;
  assetIndexing: AssetIndexingService;
  eventIndexing: EventIndexingService;
  queryService: IndexQueryService;
  searchService: IndexSearchService;
  storageBinding: IndexStorageBindingService;
  validationService: IndexValidationService;
  eventPublisher: IndexerEventPublisherService;
  readinessService: IndexerReadinessService;
}

export function createIndexerEngineServices(options?: {
  repositories?: IndexerRepositories;
  protocolRegistry?: ProtocolAdapterRegistry;
  ledgerRegistry?: LedgerAdapterRegistry;
  eventBusRegistry?: EventBusAdapterRegistry;
  storageRegistry?: StorageAdapterRegistry;
  eventBus?: typeof indexerInternalEventBus;
}): {
  repositories: IndexerRepositories;
  protocolRegistry: ProtocolAdapterRegistry;
  ledgerRegistry: LedgerAdapterRegistry;
  eventBusRegistry: EventBusAdapterRegistry;
  storageRegistry: StorageAdapterRegistry;
  services: IndexerEngineServices;
  context: IndexerServiceContext;
} {
  const repositories = options?.repositories ?? createIndexerRepositories();
  const protocolRegistry = options?.protocolRegistry ?? protocolAdapterRegistry;
  const ledgerRegistry = options?.ledgerRegistry ?? ledgerAdapterRegistry;
  const eventBusRegistry = options?.eventBusRegistry ?? eventBusAdapterRegistry;
  const storageRegistry = options?.storageRegistry ?? storageAdapterRegistry;
  const context: IndexerServiceContext = {
    eventBus: options?.eventBus ?? indexerInternalEventBus,
  };

  const services: IndexerEngineServices = {
    jobRegistry: new IndexJobRegistryService(context, repositories, eventBusRegistry),
    blockIndexing: new BlockIndexingService(context, repositories, protocolRegistry),
    transactionIndexing: new TransactionIndexingService(context, repositories, ledgerRegistry),
    addressIndexing: new AddressIndexingService(context, repositories, ledgerRegistry),
    assetIndexing: new AssetIndexingService(context, repositories, ledgerRegistry),
    eventIndexing: new EventIndexingService(context, repositories, eventBusRegistry),
    queryService: new IndexQueryService(context, repositories, storageRegistry),
    searchService: new IndexSearchService(context, storageRegistry),
    storageBinding: new IndexStorageBindingService(context, repositories),
    validationService: new IndexValidationService(context, protocolRegistry, ledgerRegistry),
    eventPublisher: new IndexerEventPublisherService(context, repositories, eventBusRegistry),
    readinessService: new IndexerReadinessService(context),
  };

  return {
    repositories,
    protocolRegistry,
    ledgerRegistry,
    eventBusRegistry,
    storageRegistry,
    services,
    context,
  };
}
